import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import FlatSwitch from "../common/FlatSwitch";
import { hapticSelection } from "../../lib/haptics";
import { isNative } from "../../lib/platform";
import { useNotificationPreferences } from "./hooks/useNotificationPreferences";
import { useUpdateNotificationPreference } from "./hooks/useUpdateNotificationPreference";
import { NotificationChannel } from "./types";

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: "Email",
  push: "Push",
};

const CHANNEL_COLUMN_WIDTH = 64;

/**
 * Category × channel switches. Renders whatever categories the server sends —
 * a category this build has never heard of is just another row. Push exists
 * only in the native app, so its column does too. Every change is optimistic
 * and rolls back if the server refuses it.
 */
const NotificationPreferencesCard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data, isLoading, isError } = useNotificationPreferences();
  const { mutate: updatePreference } = useUpdateNotificationPreference();

  const channels: NotificationChannel[] = ["email"];
  if (isNative()) channels.push("push");

  const hairline = isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.07)";
  const preferences = data?.preferences ?? [];

  return (
    <Box
      component="section"
      aria-label="Notification preferences"
      sx={{
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.9)",
        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
        borderRadius: 3,
        p: 3,
        mb: 3,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Notifications
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2 }}>
        Choose how you hear from us. Everything always reaches your inbox in the app.
      </Typography>

      {isLoading && (
        <Box aria-label="Loading notification preferences">
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Box>
      )}

      {isError && !data && (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          We couldn't load your notification preferences. Please try again later.
        </Typography>
      )}

      {preferences.length > 0 && (
        <Box>
          <Box aria-hidden sx={{ display: "flex", alignItems: "center", pb: 0.5 }}>
            <Box sx={{ flex: 1 }} />
            {channels.map((channel) => (
              <Typography
                key={channel}
                sx={{
                  width: CHANNEL_COLUMN_WIDTH,
                  flexShrink: 0,
                  textAlign: "center",
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                }}
              >
                {CHANNEL_LABELS[channel]}
              </Typography>
            ))}
          </Box>

          {preferences.map((preference) => (
            <Box
              key={preference.category}
              sx={{
                display: "flex",
                alignItems: "center",
                py: 1.25,
                borderTop: `1px solid ${hairline}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, pr: 1.5 }}>
                <Typography
                  sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}
                >
                  {preference.label}
                </Typography>
                {preference.description && (
                  <Typography
                    sx={{ mt: 0.25, fontSize: "0.8125rem", color: theme.palette.text.secondary }}
                  >
                    {preference.description}
                  </Typography>
                )}
                {preference.locked && (
                  <Typography
                    sx={{ mt: 0.25, fontSize: "0.75rem", color: theme.palette.text.secondary }}
                  >
                    Always on. These can't be turned off.
                  </Typography>
                )}
              </Box>

              {channels.map((channel) => (
                // A 44px-high label, so the 26px switch has a full touch target
                <Box
                  key={channel}
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: CHANNEL_COLUMN_WIDTH,
                    minHeight: 44,
                    flexShrink: 0,
                    cursor: preference.locked ? "not-allowed" : "pointer",
                  }}
                >
                  <FlatSwitch
                    checked={preference.channels[channel] === true}
                    disabled={preference.locked}
                    ariaLabel={`${preference.label}: ${CHANNEL_LABELS[channel]}`}
                    onChange={(enabled) => {
                      hapticSelection();
                      updatePreference({ category: preference.category, channel, enabled });
                    }}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default NotificationPreferencesCard;
